import axios from 'axios';

// Genera el token HMAC para el link público de PDF.
// Se delega al backend para no exponer el secret en el frontend.
export async function obtenerTokenPublico(tipo, id) {
  try {
    const res = await axios.get(`/api/wsp-config/generar-token/${tipo}/${id}`);
    return res.data.token;
  } catch {
    return null;
  }
}

// Construye la URL pública del PDF según el tipo de documento y formato
export function buildPublicPdfUrl(tipo, id, token, formato = 'A4') {
  const base = window.location.origin;
  return `${base}/api/public/pdf/${tipo}/${id}/${token}?formato=${formato}`;
}

// Construye la URL pública del XML firmado (comprobantes electrónicos y guías)
export function buildPublicXmlUrl(id, token, docTipo = 'comprobante') {
  const base = window.location.origin;
  const ruta = docTipo === 'guia' ? 'xml-guia' : 'xml';
  return `${base}/api/public/pdf/${ruta}/${id}/${token}`;
}

export function buildPublicCdrUrl(id, token, docTipo = 'comprobante') {
  const base = window.location.origin;
  const ruta = docTipo === 'guia' ? 'cdr-guia' : 'cdr';
  return `${base}/api/public/pdf/${ruta}/${id}/${token}`;
}

// Reemplaza variables {{var}} en la plantilla con datos reales del documento
export function buildMensaje(plantilla, vars) {
  let msg = plantilla;
  Object.entries(vars).forEach(([key, val]) => {
    msg = msg.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), val ?? '');
  });
  return msg;
}

// Formatea el número de teléfono para wa.me (solo dígitos, agrega 51 si es peruano sin código)
export function formatearTelefono(telefono) {
  if (!telefono) return '';
  const digits = telefono.replace(/\D/g, '');
  if (digits.length === 9 && digits.startsWith('9')) return `51${digits}`;
  if (digits.length === 11 && digits.startsWith('51')) return digits;
  return digits;
}

// Abre WhatsApp con el mensaje pre-llenado
export function abrirWhatsApp(telefono, mensaje) {
  const num = formatearTelefono(telefono);
  const text = encodeURIComponent(mensaje);
  const url = num ? `https://wa.me/${num}?text=${text}` : `https://wa.me/?text=${text}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

// Formatea fecha ISO a dd/mm/yyyy
// fecha_emision llega como fecha pura a medianoche UTC (ej. "2026-10-10T00:00:00.000Z").
// new Date(...).toLocaleDateString() la interpreta en el timezone LOCAL del
// navegador de quien copia el mensaje: si esa persona tiene su equipo en hora
// Perú (UTC-5), medianoche UTC retrocede al día anterior (19:00 del día de
// antes) y el mensaje sale con la fecha equivocada. Caso real: F001-2250
// ferreteriaelian mostraba 09/10 en vez de 10/10. Se extraen los componentes
// directamente del string (o con getters UTC como respaldo) para que el
// resultado no dependa del timezone de quien lo use.
export function fmtFechaWsp(fecha) {
  if (!fecha) return '';
  try {
    const m = String(fecha).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    const d = new Date(fecha);
    if (Number.isNaN(d.getTime())) return String(fecha);
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getUTCFullYear()}`;
  } catch {
    return fecha;
  }
}

// Formatea monto con separador de miles
export function fmtMontoWsp(monto, moneda) {
  const symbol = ({ USD: '$', EUR: '€', PEN: 'S/' })[moneda] || 'S/';
  const num = parseFloat(monto || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${symbol} ${num}`;
}

// Devuelve el nombre legible del tipo de documento
export function nombreTipoDocumento(tipo) {
  const TIPOS = {
    '01': 'Factura Electrónica',
    '03': 'Boleta de Venta',
    '07': 'Nota de Crédito',
    '08': 'Nota de Débito',
    '09': 'Guía de Remisión',
    '31': 'Guía de Remisión Transportista',
    'CTZ': 'Cotización',
    'NV': 'Nota de Venta',
  };
  return TIPOS[tipo] || tipo;
}
